/* Standalone planning calculator. Shopify prices are whole-package prices in cents. */
(() => {
  'use strict';

  function parseDecimal(raw) {
    const text = String(raw ?? '').trim();
    if (!text) return null;
    if (!/^\d+(?:[.,]\d{1,6})?$/.test(text)) return NaN;
    return Number(text.replace(',', '.'));
  }

  function calculate({ area, waste, coverage = null, boxPrice = null, pricePerM2 = null }) {
    if (!Number.isFinite(area) || area <= 0 || area > 1000000) throw new Error('error_area');
    if (!Number.isFinite(waste) || waste < 0 || waste > 100) throw new Error('error_waste');
    if (coverage !== null && (!Number.isFinite(coverage) || coverage <= 0 || coverage > 10000)) throw new Error('error_coverage');
    if (pricePerM2 !== null && (!Number.isFinite(pricePerM2) || pricePerM2 < 0 || pricePerM2 > 1000000)) throw new Error('error_price');
    if (boxPrice !== null && (!Number.isSafeInteger(boxPrice) || boxPrice < 0)) throw new Error('error_box_price');
    const required = area * (1 + waste / 100);
    // Remove floating-point noise at exact package boundaries, without rounding
    // area to hundredths (which could under-order a fraction of a square metre).
    const quotient = coverage === null ? null : required / coverage;
    const boxes = quotient === null ? null : Math.ceil(quotient - Number.EPSILON * Math.max(1, quotient) * 8);
    const ordered = boxes === null ? null : boxes * coverage;
    const centsPerBox = boxPrice ?? (pricePerM2 !== null && coverage !== null ? Math.round(pricePerM2 * coverage * 100) : null);
    const totalCents = boxes !== null && centsPerBox !== null ? boxes * centsPerBox : null;
    return { area, required, boxes, ordered, rounding: ordered === null ? null : Math.max(0, ordered - required), totalCents };
  }

  function measuredArea(surfaces, openings) {
    const sum = (rows) => rows.reduce((total, [length, width]) => {
      if (![length, width].every((value) => Number.isFinite(value) && value > 0 && value <= 1000)) {
        throw new Error('error_dimensions');
      }
      return total + length * width;
    }, 0);
    const gross = sum(surfaces);
    const deductions = sum(openings);
    if (deductions >= gross && deductions > 0) throw new Error('error_openings');
    return gross - deductions;
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { parseDecimal, calculate, measuredArea };
  if (typeof customElements === 'undefined' || customElements.get('tile-calculator')) return;

  class TileCalculator extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.mode = this.mode || 'area';
      this.strings = this.querySelector('[data-calculator-translations]').dataset;
      const locale = this.dataset.locale || document.documentElement.lang || 'sv';
      this.number = new Intl.NumberFormat(locale, { maximumFractionDigits: 4 });
      this.money = new Intl.NumberFormat(locale, { style: 'currency', currency: this.dataset.currency || 'SEK' });
      if (!this.querySelector('[data-surfaces] fieldset')) this.addRow('surface', false);
      this.addEventListener('input', () => this.scheduleUpdate(), { signal: this.controller.signal });
      this.addEventListener('change', () => this.update(), { signal: this.controller.signal });
      this.addEventListener('click', (event) => {
        const button = event.target.closest('button');
        if (!button) return;
        if (button.dataset.mode) {
          this.mode = button.dataset.mode;
          this.querySelectorAll('[data-mode]').forEach((item) => item.setAttribute('aria-pressed', String(item.dataset.mode === this.mode)));
          this.querySelector('[data-area-panel]').hidden = this.mode !== 'area';
          this.querySelector('[data-measure-panel]').hidden = this.mode !== 'measure';
        }
        if (button.dataset.add) this.addRow(button.dataset.add, true);
        if (button.hasAttribute('data-remove')) {
          const row = button.closest('fieldset');
          const container = row.parentElement;
          const target = container.matches('[data-surfaces]') ? 'surface' : 'opening';
          row.remove();
          this.querySelector(`[data-add="${target}"]`).focus();
        }
        this.update();
      }, { signal: this.controller.signal });
      this.querySelector('[data-calculator-ui]').hidden = false;
      this.update();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      clearTimeout(this.timer);
    }

    scheduleUpdate() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.update(), 250);
    }

    addRow(kind, focus) {
      const fragment = this.querySelector('[data-row-template]').content.cloneNode(true);
      const row = fragment.querySelector('fieldset');
      this.rowIndex = (this.rowIndex || 0) + 1;
      const title = `${kind === 'surface' ? this.strings.surface : this.strings.opening} ${this.rowIndex}`;
      row.querySelector('legend').textContent = title;
      row.querySelector('[data-remove]').setAttribute('aria-label', `${this.strings.remove} ${title.toLowerCase()}`);
      row.querySelectorAll('input').forEach((input) => input.setAttribute('aria-describedby', `Error-${this.dataset.section}`));
      this.querySelector(kind === 'surface' ? '[data-surfaces]' : '[data-openings]').append(fragment);
      if (focus) row.querySelector('input').focus();
    }

    readInput(input, { optional = false, zero = false, max = 1000000 } = {}) {
      const value = parseDecimal(input.value);
      const valid = value === null ? optional : Number.isFinite(value) && (zero ? value >= 0 : value > 0) && value <= max;
      input.setAttribute('aria-invalid', String(!valid));
      return value;
    }

    update() {
      clearTimeout(this.timer);
      const set = (name, value) => { this.querySelector(`[data-${name}]`).textContent = value; };
      const formatArea = (value) => `${this.number.format(value)} m²`;
      const error = this.querySelector('[data-error]');
      error.hidden = true;
      error.textContent = '';
      this.querySelectorAll('[aria-invalid]').forEach((input) => input.removeAttribute('aria-invalid'));
      const reset = () => {
        ['main-result', 'net', 'waste-result', 'required', 'ordered', 'rounding'].forEach((name) => set(name, '—'));
        this.querySelectorAll('[data-package-row], [data-cost-row]').forEach((row) => { row.hidden = true; });
      };
      try {
        let area;
        if (this.mode === 'area') {
          const input = this.querySelector('[data-area]');
          if (!input.value.trim()) {
            reset();
            set('result-caption', this.strings.start);
            return;
          }
          area = this.readInput(input);
        } else {
          const readRows = (selector) => [...this.querySelectorAll(`${selector} fieldset`)].map((row) => {
            const dimensions = ['length', 'width'].map((name) => this.readInput(row.querySelector(`[data-${name}]`), { max: 1000 }));
            row.querySelector('[data-row-area]').textContent = dimensions.every((n) => Number.isFinite(n) && n > 0 && n <= 1000) ? formatArea(dimensions[0] * dimensions[1]) : '— m²';
            return dimensions;
          });
          area = measuredArea(readRows('[data-surfaces]'), readRows('[data-openings]'));
        }
        const waste = this.readInput(this.querySelector('[data-waste]'), { zero: true, max: 100 });
        const coverage = this.readInput(this.querySelector('input[data-coverage]'), { optional: true, max: 10000 });
        const pricePerM2 = this.readInput(this.querySelector('input[data-price]'), { optional: true, zero: true });
        const result = calculate({ area, waste, coverage, pricePerM2 });
        set('main-result', result.boxes === null ? formatArea(result.required) : this.strings.packages.replace('__count__', this.number.format(result.boxes)));
        set('result-caption', result.boxes === null ? this.strings.withoutCoverage : this.strings.covered.replace('__area__', formatArea(result.ordered)));
        set('net', formatArea(area));
        set('waste-result', `${this.number.format(waste)} %`);
        set('required', formatArea(result.required));
        this.querySelectorAll('[data-package-row]').forEach((row) => { row.hidden = result.boxes === null; });
        set('ordered', result.ordered === null ? '—' : formatArea(result.ordered));
        set('rounding', result.rounding === null ? '—' : formatArea(result.rounding));
        this.querySelector('[data-cost-row]').hidden = result.totalCents === null;
        set('cost', result.totalCents === null ? '' : this.money.format(result.totalCents / 100));
      } catch (problem) {
        reset();
        set('result-caption', this.strings.check);
        error.textContent = this.strings[problem.message.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())] || this.strings.check;
        error.hidden = false;
      }
    }
  }
  customElements.define('tile-calculator', TileCalculator);
})();
